import { useState, useEffect } from "react";
import { supabase } from "../services/supabase";

export const useSessionData = (sessionId) => {
  const [sessionParticipants, setSessionParticipants] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [payments, setPayments] = useState([]);
  const [fieldHours, setFieldHours] = useState([]);
  const [excludeBoxUsers, setExcludeBoxUsers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isNotFound, setIsNotFound] = useState(false);

  const fetchSessionData = async (isBackground = false, ignore = { current: false }) => {
    if (!sessionId) return;
    if (!isBackground) setLoading(true);
    try {
      setError(null);

      // Verify session exists first
      const { data: sessionInfo, error: sessionError } = await supabase
        .from("sessions")
        .select("id")
        .eq("id", sessionId)
        .maybeSingle();

      if (ignore.current) return;

      // isNotFound should ONLY be true if we successfully checked and it was missing
      // If there's an actual sessionError (e.g. network), we handle it as a standard error
      if (!sessionInfo && !sessionError) {
        setIsNotFound(true);
        return;
      }

      if (sessionError) {
        throw sessionError;
      }

      // Fetch everything else in parallel
      const [participantsRes, expensesRes, paymentsRes, settingsRes] = await Promise.all([
        supabase
          .from("session_participants")
          .select("participant_id, participants(*)")
          .eq("session_id", sessionId),
        supabase
          .from("expenses")
          .select("*, field_hours(*)")
          .eq("session_id", sessionId)
          .order("created_at", { ascending: false }),
        supabase
          .from("session_payments")
          .select("*")
          .eq("session_id", sessionId),
        supabase
          .from("session_participant_settings")
          .select("*")
          .eq("session_id", sessionId),
      ]);

      if (ignore.current) return;

      if (participantsRes.data) {
        setSessionParticipants(participantsRes.data.map((sp) => sp.participants));
      }

      const currentExpenses = expensesRes.data || [];
      setExpenses(currentExpenses);

      if (paymentsRes.data) {
        setPayments(paymentsRes.data);
      }

      // Automatically extract and flatten field_hours from the nested expenses query
      const allFieldHours = currentExpenses.reduce((acc, exp) => {
        if (exp.field_hours) {
          return [...acc, ...exp.field_hours];
        }
        return acc;
      }, []);

      setFieldHours(allFieldHours);

      if (settingsRes.data && settingsRes.data.length > 0) {
        const settingsMap = {};
        settingsRes.data.forEach((s) => {
          settingsMap[s.participant_id] = s.exclude_box_cost;
        });
        setExcludeBoxUsers(settingsMap);
      } else {
        setExcludeBoxUsers({});
      }
    } catch (err) {
      if (ignore.current) return;
      console.error("Error fetching session data:", err);
      // Suppress background errors to prevent flicker, but allow initial load error
      if (!isBackground) {
        setError(err.message || "failed_sync_session");
      }
    } finally {
      if (!ignore.current && !isBackground) setLoading(false);
    }
  };

  useEffect(() => {
    const ignore = { current: false };
    fetchSessionData(false, ignore);

    if (!sessionId) return;

    const channel = supabase
      .channel(`session-${sessionId}-changes`)
      .on(
        "postgres_changes",
        { event: "DELETE", schema: "public", table: "sessions", filter: `id=eq.${sessionId}` },
        () => {
          if (!ignore.current) setIsNotFound(true);
        }
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "session_participants", filter: `session_id=eq.${sessionId}` },
        () => fetchSessionData(true, ignore)
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "expenses", filter: `session_id=eq.${sessionId}` },
        () => fetchSessionData(true, ignore)
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "session_payments", filter: `session_id=eq.${sessionId}` },
        () => fetchSessionData(true, ignore)
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "field_hours", filter: `session_id=eq.${sessionId}` },
        () => fetchSessionData(true, ignore)
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "session_participant_settings", filter: `session_id=eq.${sessionId}` },
        () => fetchSessionData(true, ignore)
      )
      .subscribe();

    return () => {
      ignore.current = true;
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return {
    sessionParticipants,
    expenses,
    payments,
    fieldHours,
    excludeBoxUsers,
    loading,
    error,
    isNotFound,
    refreshSession: (bg) => fetchSessionData(bg, { current: false }),
    setFieldHours,
    setExcludeBoxUsers,
  };
};
